import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Plus,
  Settings,
  MessageSquare,
  Users,
  CheckCircle,
  ExternalLink,
  X,
  Bot,
  Loader2,
  Send,
  Eye,
  EyeOff,
  Copy,
  Check,
  Globe,
  Key,
  Webhook,
  Zap,
  Trash2,
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Palette,
  Code,
  AlertTriangle,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { channelsService, Channel, MetaPage } from '../../services/channels.service'
import { agentsService } from '../../services/agents.service'

interface Agent {
  id: string
  name: string
  type: string
  status: string
}

type SortKey = 'name' | 'channel_type' | 'status' | 'created_at'
type SortDir = 'asc' | 'desc'
type FilterType = 'all' | 'telegram' | 'facebook' | 'instagram' | 'whatsapp' | 'custom'

function AgentMultiSelect({
  agents,
  selectedIds,
  onChange,
  placeholder = 'Select agents...',
  accentColor = 'amber',
  size = 'normal',
}: {
  agents: Agent[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  placeholder?: string
  accentColor?: 'amber' | 'sky' | 'indigo' | 'purple'
  size?: 'normal' | 'compact'
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter(i => i !== id) : [...selectedIds, id])
  }

  const colorMap = {
    amber: { pill: 'bg-amber-50 text-amber-700', ring: 'ring-amber-500', check: 'text-amber-600' },
    sky: { pill: 'bg-sky-50 text-sky-700', ring: 'ring-sky-500', check: 'text-sky-600' },
    indigo: { pill: 'bg-indigo-50 text-indigo-700', ring: 'ring-indigo-500', check: 'text-indigo-600' },
    purple: { pill: 'bg-purple-50 text-purple-700', ring: 'ring-purple-500', check: 'text-purple-600' },
  }
  const colors = colorMap[accentColor]
  const isCompact = size === 'compact'

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-1.5 border-2 border-[#1a1a2e] bg-white rounded-xl outline-none transition-all hover:border-gray-400 focus:ring-2 ${colors.ring} focus:border-transparent ${
          isCompact ? 'px-2 py-1 min-h-[32px]' : 'px-3 py-2 min-h-[42px]'
        }`}
      >
        <div className="flex-1 flex flex-wrap gap-1 min-w-0">
          {selectedIds.length === 0 ? (
            <span className={`text-gray-400 ${isCompact ? 'text-[11px]' : 'text-sm'}`}>{placeholder}</span>
          ) : (
            selectedIds.map(id => {
              const agent = agents.find(a => a.id === id)
              return (
                <span key={id} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ${colors.pill} ${isCompact ? 'text-[10px]' : 'text-xs'} font-medium`}>
                  <Bot className={`${isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
                  <span className="truncate max-w-[80px]">{agent?.name || '?'}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(id) }}
                    className="ml-0.5 hover:opacity-70"
                  >
                    <X className={`${isCompact ? 'w-2.5 h-2.5' : 'w-3 h-3'}`} />
                  </button>
                </span>
              )
            })
          )}
        </div>
        <ChevronDown className={`flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''} ${isCompact ? 'w-3 h-3' : 'w-4 h-4'}`} />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border-2 border-[#1a1a2e] rounded-xl shadow-[5px_5px_0px_#1a1a2e] max-h-[200px] overflow-y-auto">
          {agents.length === 0 ? (
            <p className="text-xs text-gray-400 p-3 text-center">No active agents</p>
          ) : (
            agents.map(agent => {
              const selected = selectedIds.includes(agent.id)
              return (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => toggle(agent.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    selected ? 'bg-gray-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    selected ? `${colors.check} border-current bg-current/10` : 'border-gray-300'
                  }`}>
                    {selected && <Check className="w-3 h-3" />}
                  </div>
                  <Bot className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-900 truncate">{agent.name}</span>
                  <span className="text-xs text-gray-400 capitalize flex-shrink-0">({agent.type})</span>
                </button>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}

export function ChannelsPage() {
  const { id: channelIdFromUrl } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [channels, setChannels] = useState<Channel[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [showConnectModal, setShowConnectModal] = useState(false)
  const [showTelegramModal, setShowTelegramModal] = useState(false)
  const [showCustomModal, setShowCustomModal] = useState(false)
  const [showAgentModal, setShowAgentModal] = useState<string | null>(null)
  const [showDetailModal, setShowDetailModal] = useState<Channel | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Telegram connect form state
  const [botToken, setBotToken] = useState('')
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([])
  const [botName, setBotName] = useState('')
  const [isConnecting, setIsConnecting] = useState(false)
  const [showToken, setShowToken] = useState(false)

  // Custom channel form state
  const [customName, setCustomName] = useState('')
  const [customWebhookUrl, setCustomWebhookUrl] = useState('')
  const [customAgentIds, setCustomAgentIds] = useState<string[]>([])
  const [isCreatingCustom, setIsCreatingCustom] = useState(false)

  // Agent management state
  const [addingAgentId, setAddingAgentId] = useState('')
  const [isAddingAgent, setIsAddingAgent] = useState(false)

  // Disconnect / regenerate state
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null)

  // Copied token state
  const [copiedToken, setCopiedToken] = useState<string | null>(null)

  // Meta connect state
  const [showMetaModal, setShowMetaModal] = useState(false)
  const [metaStep, setMetaStep] = useState<'auth' | 'pages' | 'connecting'>('auth')
  const [metaPages, setMetaPages] = useState<MetaPage[]>([])
  const [metaPageConfig, setMetaPageConfig] = useState<Record<string, { facebook: boolean; instagram: boolean; agent_ids: string[] }>>({})
  const [isMetaLoading, setIsMetaLoading] = useState(false)
  const [metaInitChannelType, setMetaInitChannelType] = useState<'facebook' | 'instagram'>('facebook')
  const [metaConnectProgress, setMetaConnectProgress] = useState('')

  // WhatsApp connect state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false)
  const [whatsappAgentIds, setWhatsappAgentIds] = useState<string[]>([])
  const [isWhatsAppLoading, setIsWhatsAppLoading] = useState(false)
  const [whatsappStep, setWhatsappStep] = useState<'auth' | 'phone' | 'connecting'>('auth')
  const [whatsappPhones, setWhatsappPhones] = useState<{ id: string; display_phone_number: string; verified_name: string }[]>([])
  const [selectedWhatsappPhoneIds, setSelectedWhatsappPhoneIds] = useState<Set<string>>(new Set())
  const [whatsappWabaName, setWhatsappWabaName] = useState('')
  const [whatsappMessage, setWhatsappMessage] = useState('')

  // Edit modal state
  const [editName, setEditName] = useState('')
  const [editWebhookUrl, setEditWebhookUrl] = useState('')
  const [editResponseMode, setEditResponseMode] = useState<'sync' | 'async'>('sync')
  const [isSaving, setIsSaving] = useState(false)

  // Widget config state
  const [widgetTab, setWidgetTab] = useState<'theme' | 'embed'>('theme')
  const [widgetColor, setWidgetColor] = useState('#2563eb')
  const [widgetBubbleText, setWidgetBubbleText] = useState('Chat with us')
  const [widgetWelcome, setWidgetWelcome] = useState('Hi! How can I help?')
  const [widgetHeaderText, setWidgetHeaderText] = useState('Support Agent')
  const [widgetPosition, setWidgetPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right')
  const [copiedEmbed, setCopiedEmbed] = useState<string | null>(null)

  // Table state
  const [sortKey, setSortKey] = useState<SortKey>('created_at')
  const [sortDir, setSortDir] = useState<SortDir>('desc')
  const [filterType, setFilterType] = useState<FilterType>('all')

  useEffect(() => {
    fetchChannels()
    fetchAgents()
  }, [])

  const fetchChannels = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await channelsService.getChannels()
      setChannels(data)
    } catch (err) {
      setError('Failed to load channels')
      console.error('Error fetching channels:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAgents = async () => {
    try {
      const data = await agentsService.getAgents()
      setAgents(data as Agent[])
    } catch (err) {
      console.error('Error fetching agents:', err)
    }
  }

  const getChannelIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'telegram':
        return <Send className="w-4 h-4 text-white" />
      case 'custom':
        return <Zap className="w-4 h-4 text-white" />
      case 'instagram':
        return <span className="text-sm">📷</span>
      case 'facebook':
        return <span className="text-sm">👤</span>
      case 'whatsapp':
        return <span className="text-sm">💬</span>
      default:
        return <Globe className="w-4 h-4 text-white" />
    }
  }

  const getChannelIconBg = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'telegram':
        return 'bg-sky-500'
      case 'custom':
        return 'bg-indigo-500'
      case 'instagram':
        return 'bg-purple-500'
      case 'facebook':
        return 'bg-blue-500'
      case 'whatsapp':
        return 'bg-green-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'connected':
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-50 text-green-700">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            Active
          </span>
        )
      case 'disconnected':
      case 'inactive':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
            <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
            Inactive
          </span>
        )
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
            Error
          </span>
        )
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
            {status}
          </span>
        )
    }
  }

  // Sort and filter
  const filteredChannels = useMemo(() => {
    let list = [...channels]
    if (filterType !== 'all') {
      list = list.filter(ch => ch.channel_type === filterType)
    }
    list.sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name)
          break
        case 'channel_type':
          cmp = a.channel_type.localeCompare(b.channel_type)
          break
        case 'status':
          cmp = a.status.localeCompare(b.status)
          break
        case 'created_at':
          cmp = new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return list
  }, [channels, filterType, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const SortIcon = ({ field }: { field: SortKey }) => {
    if (sortKey !== field) return <ArrowUpDown className="w-3 h-3 text-gray-300" />
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3 text-amber-600" /> : <ChevronDown className="w-3 h-3 text-amber-600" />
  }

  const handleOpenTelegramConnect = async () => {
    setShowConnectModal(false)
    navigate('/channels')
    setShowTelegramModal(true)
    setBotToken('')
    setSelectedAgentIds([])
    setBotName('')
    setShowToken(false)
    await fetchAgents()
  }

  const handleOpenCustomChannel = async () => {
    setShowConnectModal(false)
    navigate('/channels')
    setShowCustomModal(true)
    setCustomName('')
    setCustomWebhookUrl('')
    setCustomAgentIds([])
    await fetchAgents()
  }

  const handleOpenMetaConnect = async (channelType: 'facebook' | 'instagram') => {
    setShowConnectModal(false)
    navigate('/channels')
    setMetaInitChannelType(channelType)
    setMetaStep('auth')
    setMetaPages([])
    setMetaPageConfig({})
    setMetaConnectProgress('')
    setShowMetaModal(true)
    await fetchAgents()
  }

  const handleOpenWhatsAppConnect = async () => {
    setShowConnectModal(false)
    navigate('/channels')
    setWhatsappAgentIds([])
    setWhatsappStep('auth')
    setWhatsappPhones([])
    setSelectedWhatsappPhoneIds(new Set())
    setWhatsappWabaName('')
    setShowWhatsAppModal(true)
    await fetchAgents()
  }

  const handleWhatsAppAuth = async () => {
    setIsWhatsAppLoading(true)
    try {
      const redirectUri = `${window.location.origin}/channels/meta/callback`
      const { url } = await channelsService.getWhatsAppOAuthUrl(redirectUri)

      const width = 600, height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(url, 'whatsapp_oauth', `width=${width},height=${height},left=${left},top=${top}`)

      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type === 'META_OAUTH_CALLBACK' && event.data?.code) {
          window.removeEventListener('message', handleMessage)
          popup?.close()

          // Discover WABAs + phone numbers
          try {
            const discovered = await channelsService.discoverWhatsApp(event.data.code, redirectUri)
            setWhatsappWabaName(discovered.waba_name || '')
            setWhatsappPhones(discovered.phone_numbers || [])
            setWhatsappMessage(discovered.message || '')
            // Pre-select all phone numbers (user can deselect)
            setSelectedWhatsappPhoneIds(new Set((discovered.phone_numbers || []).map(p => p.id)))
            // Always go to phone step — it handles empty states with friendly messages
            setWhatsappStep('phone')
          } catch (err: any) {
            toast.error(err.message || 'Failed to connect to WhatsApp')
            setWhatsappStep('auth')
          }
          setIsWhatsAppLoading(false)
        }
        if (event.data?.type === 'META_OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage)
          popup?.close()
          toast.error('WhatsApp authorization failed')
          setIsWhatsAppLoading(false)
        }
      }
      window.addEventListener('message', handleMessage)

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          setIsWhatsAppLoading(false)
        }
      }, 1000)
    } catch (err: any) {
      toast.error(err.message || 'Failed to start WhatsApp authorization')
      setIsWhatsAppLoading(false)
    }
  }

  const handleWhatsAppConnect = async () => {
    const selectedPhones = whatsappPhones.filter(p => selectedWhatsappPhoneIds.has(p.id))
    if (selectedPhones.length === 0) return
    setWhatsappStep('connecting')
    setIsWhatsAppLoading(true)
    try {
      let connected = 0
      for (const phone of selectedPhones) {
        try {
          await channelsService.connectWhatsAppPhone(
            phone.id,
            phone.display_phone_number,
            phone.verified_name,
            whatsappAgentIds
          )
          connected++
        } catch (err: any) {
          toast.error(`${phone.display_phone_number}: ${err.message || 'Failed to connect'}`)
        }
      }
      if (connected > 0) {
        toast.success(`${connected} WhatsApp number${connected > 1 ? 's' : ''} connected!`)
      }
      setShowWhatsAppModal(false)
      await fetchChannels()
    } catch (err: any) {
      toast.error(err.message || 'Failed to connect WhatsApp')
      setWhatsappStep('phone')
    } finally {
      setIsWhatsAppLoading(false)
    }
  }

  const handleMetaAuth = async () => {
    setIsMetaLoading(true)
    try {
      const redirectUri = `${window.location.origin}/channels/meta/callback`
      const { url } = await channelsService.getMetaOAuthUrl(redirectUri)

      const width = 600, height = 700
      const left = window.screenX + (window.outerWidth - width) / 2
      const top = window.screenY + (window.outerHeight - height) / 2
      const popup = window.open(url, 'meta_oauth', `width=${width},height=${height},left=${left},top=${top}`)

      const handleMessage = async (event: MessageEvent) => {
        if (event.data?.type === 'META_OAUTH_CALLBACK' && event.data?.code) {
          window.removeEventListener('message', handleMessage)
          popup?.close()

          try {
            // Exchange code for token
            const result = await channelsService.handleMetaCallback(event.data.code, redirectUri)

            // Facebook/Instagram flow — show pages
            setMetaStep('connecting')
            setMetaConnectProgress('Fetching your pages and connections...')
            setMetaPages(result.pages)

            const config: Record<string, { facebook: boolean; instagram: boolean; agent_ids: string[] }> = {}
            result.pages.forEach(page => {
              config[page.id] = {
                facebook: metaInitChannelType === 'facebook',
                instagram: metaInitChannelType === 'instagram' && !!page.instagram_account,
                agent_ids: [],
              }
            })
            setMetaPageConfig(config)
            setMetaStep('pages')
          } catch (err: any) {
            toast.error(err.message || 'Failed to process Meta authorization')
            setMetaStep('auth')
          } finally {
            setIsMetaLoading(false)
            setMetaConnectProgress('')
          }
        }
        if (event.data?.type === 'META_OAUTH_ERROR') {
          window.removeEventListener('message', handleMessage)
          popup?.close()
          toast.error('Facebook authorization was denied or failed')
          setIsMetaLoading(false)
        }
      }
      window.addEventListener('message', handleMessage)

      const checkClosed = setInterval(() => {
        if (popup?.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          setIsMetaLoading(false)
          setMetaConnectProgress('')
        }
      }, 1000)
    } catch (err: any) {
      toast.error(err.message || 'Failed to start Meta authorization')
      setIsMetaLoading(false)
    }
  }

  const handleMetaConnect = async () => {
    const pagesToConnect: Array<{ page: MetaPage; type: 'facebook' | 'instagram'; agent_ids: string[] }> = []
    for (const page of metaPages) {
      const config = metaPageConfig[page.id]
      if (!config) continue
      const alreadyFb = channels.some(ch => ch.channel_type === 'facebook' && ch.credentials?.page_id === page.id)
      const alreadyIg = channels.some(ch => ch.channel_type === 'instagram' && ch.credentials?.page_id === page.id)

      if (config.facebook && !alreadyFb) {
        if (config.agent_ids.length === 0) {
          toast.error(`Please select at least one agent for "${page.name}" (Facebook)`)
          return
        }
        pagesToConnect.push({ page, type: 'facebook', agent_ids: config.agent_ids })
      }
      if (config.instagram && !alreadyIg && page.instagram_account) {
        if (config.agent_ids.length === 0) {
          toast.error(`Please select at least one agent for "${page.name}" (Instagram)`)
          return
        }
        pagesToConnect.push({ page, type: 'instagram', agent_ids: config.agent_ids })
      }
    }

    if (pagesToConnect.length === 0) {
      toast.error('No pages selected')
      return
    }

    setMetaStep('connecting')
    setIsMetaLoading(true)

    try {
      let connected = 0
      for (const { page, type, agent_ids } of pagesToConnect) {
        setMetaConnectProgress(`Connecting ${page.name} (${type})...`)
        await channelsService.connectMeta({
          page_id: page.id,
          page_name: type === 'instagram' ? (page.instagram_account?.username || page.name) : page.name,
          channel_type: type,
          agent_ids,
          instagram_account_id: type === 'instagram' ? page.instagram_account?.id : undefined,
        })
        connected++
      }

      toast.success(`${connected} channel${connected > 1 ? 's' : ''} connected!`)
      setShowMetaModal(false)
      await fetchChannels()
    } catch (err: any) {
      toast.error(err.message || 'Failed to connect Meta channel')
      setMetaStep('pages')
    } finally {
      setIsMetaLoading(false)
      setMetaConnectProgress('')
    }
  }

  const handleConnect = (_type: string) => {
    if (_type === 'telegram') { handleOpenTelegramConnect(); return }
    if (_type === 'custom') { handleOpenCustomChannel(); return }
    if (_type === 'facebook' || _type === 'instagram') { handleOpenMetaConnect(_type); return }
    if (_type === 'whatsapp') { handleOpenWhatsAppConnect(); return }
    toast.error(`${_type} integration coming soon`)
  }

  const handleTelegramConnect = async () => {
    if (!botToken.trim()) { toast.error('Please enter a bot token'); return }

    setIsConnecting(true)
    try {
      await channelsService.connectTelegram(botToken.trim(), selectedAgentIds.length > 0 ? selectedAgentIds : [], botName.trim() || undefined)
      toast.success('Telegram bot connected successfully!')
      setShowTelegramModal(false)
      await fetchChannels()
    } catch (err: any) {
      toast.error(err.message || 'Failed to connect Telegram bot')
    } finally {
      setIsConnecting(false)
    }
  }

  const handleCreateCustomChannel = async () => {
    if (!customName.trim()) { toast.error('Please enter a channel name'); return }

    setIsCreatingCustom(true)
    try {
      await channelsService.createCustomChannel(customName.trim(), customWebhookUrl.trim() || undefined, customAgentIds.length > 0 ? customAgentIds : undefined)
      toast.success('Custom API channel created!')
      setShowCustomModal(false)
      await fetchChannels()
    } catch (err: any) {
      toast.error(err.message || 'Failed to create custom channel')
    } finally {
      setIsCreatingCustom(false)
    }
  }

  const handleDisconnect = async (channelId: string, channelName: string) => {
    if (!confirm(`Disconnect "${channelName}"? The channel will stop processing messages.`)) return

    setDisconnectingId(channelId)
    try {
      await channelsService.disconnectChannel(channelId)
      toast.success('Channel disconnected')
      await fetchChannels()
    } catch (err) {
      toast.error('Failed to disconnect channel')
    } finally {
      setDisconnectingId(null)
    }
  }

  const handleRegenerateToken = async (channelId: string) => {
    if (!confirm('Regenerate channel token? The old token will stop working immediately.')) return

    setRegeneratingId(channelId)
    try {
      await channelsService.regenerateToken(channelId)
      toast.success('Token regenerated')
      await fetchChannels()
    } catch (err) {
      toast.error('Failed to regenerate token')
    } finally {
      setRegeneratingId(null)
    }
  }

  const handleAddAgent = async (channelId: string) => {
    if (!addingAgentId) return

    setIsAddingAgent(true)
    try {
      await channelsService.addChannelAgent(channelId, addingAgentId)
      toast.success('Agent added to channel')
      setAddingAgentId('')
      await fetchChannels()
    } catch (err) {
      toast.error('Failed to add agent')
    } finally {
      setIsAddingAgent(false)
    }
  }

  const handleRemoveAgent = async (channelId: string, agentId: string) => {
    try {
      await channelsService.removeChannelAgent(channelId, agentId)
      toast.success('Agent removed from channel')
      await fetchChannels()
    } catch (err) {
      toast.error('Failed to remove agent')
    }
  }

  const openConfigModal = useCallback((channel: Channel) => {
    setEditName(channel.name)
    setEditWebhookUrl(channel.credentials?.webhook_url || '')
    setEditResponseMode(getResponseMode(channel) as 'sync' | 'async')
    // Init widget state from settings
    const w = (channel.settings as any)?.widget || {}
    setWidgetColor(w.primary_color || '#2563eb')
    setWidgetBubbleText(w.bubble_text || 'Chat with us')
    setWidgetWelcome(w.welcome_message || 'Hi! How can I help?')
    setWidgetHeaderText(w.header_text || 'Support Agent')
    setWidgetPosition(w.position || 'bottom-right')
    setWidgetTab('theme')
    setCopiedEmbed(null)
    setShowDetailModal(channel)
    navigate('/channels/' + channel.id)
  }, [navigate])

  const closeConfigModal = useCallback(() => {
    setShowDetailModal(null)
    navigate('/channels')
  }, [navigate])

  const closeConnectModal = useCallback(() => {
    setShowConnectModal(false)
    navigate('/channels')
  }, [navigate])

  // Auto-open channel from URL param
  useEffect(() => {
    if (channelIdFromUrl === 'new') {
      setShowConnectModal(true)
      return
    }
    if (channelIdFromUrl && channels.length > 0) {
      const channel = channels.find(c => c.id === channelIdFromUrl)
      if (channel && showDetailModal?.id !== channelIdFromUrl) {
        openConfigModal(channel)
      }
    }
  }, [channelIdFromUrl, channels, openConfigModal])

  const handleSaveConfig = async () => {
    if (!showDetailModal) return
    setIsSaving(true)
    try {
      const updates: Record<string, any> = {}
      if (editName.trim() && editName !== showDetailModal.name) updates.name = editName.trim()
      if (showDetailModal.channel_type === 'custom') {
        const currentWebhook = showDetailModal.credentials?.webhook_url || ''
        if (editWebhookUrl.trim() !== currentWebhook) updates.webhook_url = editWebhookUrl.trim() || ''
        const currentMode = getResponseMode(showDetailModal)
        if (editResponseMode !== currentMode) updates.response_mode = editResponseMode
      }
      // Widget settings for custom channels with token
      if (showDetailModal.channel_type === 'custom' && showDetailModal.channel_token) {
        const currentWidget = (showDetailModal.settings as any)?.widget || {}
        const newWidget = {
          primary_color: widgetColor,
          bubble_text: widgetBubbleText,
          welcome_message: widgetWelcome,
          header_text: widgetHeaderText,
          position: widgetPosition,
        }
        const changed = newWidget.primary_color !== (currentWidget.primary_color || '#2563eb') ||
          newWidget.bubble_text !== (currentWidget.bubble_text || 'Chat with us') ||
          newWidget.welcome_message !== (currentWidget.welcome_message || 'Hi! How can I help?') ||
          newWidget.header_text !== (currentWidget.header_text || 'Support Agent') ||
          newWidget.position !== (currentWidget.position || 'bottom-right')
        if (changed) {
          updates.settings = { widget: newWidget }
        }
      }
      if (Object.keys(updates).length === 0) {
        toast.success('No changes to save')
        closeConfigModal()
        return
      }
      await channelsService.updateChannel(showDetailModal.id, updates)
      toast.success('Channel updated')
      closeConfigModal()
      await fetchChannels()
    } catch (err: any) {
      toast.error(err.message || 'Failed to update channel')
    } finally {
      setIsSaving(false)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    setCopiedToken(text)
    toast.success(`${label} copied!`)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const copyEmbed = (text: string, key: string) => {
    navigator.clipboard.writeText(text)
    setCopiedEmbed(key)
    toast.success('Copied!')
    setTimeout(() => setCopiedEmbed(null), 2000)
  }

  const getApiBase = () => {
    return (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787/api').replace(/\/api$/, '')
  }

  const getAgentName = (agentId?: string) => {
    if (!agentId) return null
    const agent = agents.find(a => a.id === agentId)
    return agent?.name || null
  }

  const getResponseMode = (channel: Channel) => {
    if (channel.credentials?.webhook_url) return 'async'
    if (channel.settings?.response_mode === 'async') return 'async'
    return 'sync'
  }

  const connectedChannels = channels.filter(ch => ch.status === 'connected' || ch.status === 'active')
  const customChannels = channels.filter(ch => ch.channel_type === 'custom')
  const activeAgents = agents.filter(a => a.status === 'active' || a.status === 'testing')

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = { all: channels.length }
    for (const ch of channels) {
      counts[ch.channel_type] = (counts[ch.channel_type] || 0) + 1
    }
    return counts
  }, [channels])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Channels</h1>
          <p className="text-gray-500 mt-1">Connect and manage your messaging channels</p>
        </div>
        <button
          onClick={() => navigate('/channels/new')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
        >
          <Plus className="w-4 h-4" />
          Connect Channel
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-green-50 rounded-xl">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{connectedChannels.length}</p>
              <p className="text-sm text-gray-500">Connected</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-50 rounded-xl">
              <Zap className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{customChannels.length}</p>
              <p className="text-sm text-gray-500">Custom API</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-50 rounded-xl">
              <Users className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{channels.length}</p>
              <p className="text-sm text-gray-500">Total Channels</p>
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
        </div>
      ) : channels.length === 0 ? (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] p-12">
          <div className="text-center max-w-md mx-auto">
            <div className="w-16 h-16 rounded-xl bg-gray-50 flex items-center justify-center mx-auto mb-6">
              <MessageSquare className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No channels configured</h3>
            <p className="text-gray-500 mb-6">Connect your first messaging channel to start receiving messages</p>
            <button
              onClick={() => navigate('/channels/new')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]"
            >
              <Plus className="w-4 h-4" />
              Connect Channel
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] overflow-hidden">
          {/* Filter Tabs */}
          <div className="px-5 pt-4 pb-3 border-b-2 border-[#1a1a2e] flex items-center gap-2 flex-wrap">
            {(['all', 'telegram', 'facebook', 'instagram', 'whatsapp', 'custom'] as FilterType[]).map(type => {
              const count = filterCounts[type] || 0
              if (type !== 'all' && count === 0) return null
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    filterType === type
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {type === 'all' ? 'All' : type === 'telegram' ? 'Telegram' : type === 'facebook' ? 'Facebook' : type === 'instagram' ? 'Instagram' : type === 'whatsapp' ? 'WhatsApp' : 'Custom'}
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${filterType === type ? 'bg-amber-200 text-amber-800' : 'bg-gray-200 text-gray-600'}`}>
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-[#1a1a2e]">
                  <th className="px-5 py-3 text-left">
                    <button onClick={() => toggleSort('channel_type')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
                      Type <SortIcon field="channel_type" />
                    </button>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <button onClick={() => toggleSort('name')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
                      Name <SortIcon field="name" />
                    </button>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <button onClick={() => toggleSort('status')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
                      Status <SortIcon field="status" />
                    </button>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Agents</span>
                  </th>
                  <th className="px-5 py-3 text-left">
                    <button onClick={() => toggleSort('created_at')} className="flex items-center gap-1 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700">
                      Created <SortIcon field="created_at" />
                    </button>
                  </th>
                  <th className="px-5 py-3 text-right">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredChannels.map((channel) => {
                  const channelAgents = channel.agents || []
                  return (
                    <tr key={channel.id} className="hover:bg-gray-50/50 transition-colors group">
                      {/* Type Icon */}
                      <td className="px-5 py-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getChannelIconBg(channel.channel_type)}`}>
                          {getChannelIcon(channel.channel_type)}
                        </div>
                      </td>
                      {/* Name */}
                      <td className="px-5 py-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{channel.name}</p>
                          <p className="text-xs text-gray-400">
                            {channel.credentials?.bot_username
                              ? `@${channel.credentials.bot_username}`
                              : channel.credentials?.page_name
                                ? channel.credentials.page_name
                                : channel.channel_type === 'custom'
                                  ? 'Custom API'
                                  : channel.channel_type}
                          </p>
                        </div>
                      </td>
                      {/* Status */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1.5">
                          {getStatusBadge(channel.status)}
                          {Boolean(channel.settings?.last_error) && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600" title={String(channel.settings.last_error)}>
                              <AlertTriangle className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Agents */}
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {channelAgents.length > 0 ? (
                            channelAgents.slice(0, 3).map((ca) => (
                              <span key={ca.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[11px] font-medium">
                                <Bot className="w-2.5 h-2.5" />
                                {getAgentName(ca.agent_id) || '?'}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-gray-300">--</span>
                          )}
                          {channelAgents.length > 3 && (
                            <span className="text-[11px] text-gray-400">+{channelAgents.length - 3}</span>
                          )}
                        </div>
                      </td>
                      {/* Created */}
                      <td className="px-5 py-3">
                        <span className="text-xs text-gray-500">
                          {new Date(channel.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </td>
                      {/* Actions */}
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setShowAgentModal(channel.id)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="Manage agents"
                          >
                            <Bot className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openConfigModal(channel)}
                            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Configure"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                          {channel.channel_token && (
                            <button
                              onClick={() => handleRegenerateToken(channel.id)}
                              disabled={regeneratingId === channel.id}
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Regenerate token"
                            >
                              {regeneratingId === channel.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                            </button>
                          )}
                          {(channel.status === 'connected' || channel.status === 'active') && (
                            <button
                              onClick={() => handleDisconnect(channel.id, channel.name)}
                              disabled={disconnectingId === channel.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Disconnect"
                            >
                              {disconnectingId === channel.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {filteredChannels.length === 0 && (
            <div className="py-8 text-center text-sm text-gray-400">
              No {filterType !== 'all' ? filterType : ''} channels found
            </div>
          )}
        </div>
      )}

      {/* ====================== MODALS ====================== */}

      {/* Connect Modal -- Channel Type Picker */}
      {showConnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={closeConnectModal} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Connect a Channel</h2>
              <button onClick={closeConnectModal} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { type: 'custom' as const, label: 'Custom API Channel', desc: 'Create an API endpoint with sync or async (webhook) responses', icon: <Zap className="w-6 h-6 text-white" />, bg: 'bg-indigo-500', border: 'border-indigo-200', hover: 'hover:bg-indigo-100', bgLight: 'bg-indigo-50' },
                { type: 'telegram' as const, label: 'Telegram', desc: 'Connect your Telegram bot via BotFather token', icon: <Send className="w-6 h-6 text-white" />, bg: 'bg-sky-500', border: 'border-sky-200', hover: 'hover:bg-sky-100', bgLight: 'bg-sky-50' },
                { type: 'facebook' as const, label: 'Facebook Messenger', desc: 'Connect your Facebook page for Messenger', icon: <span className="text-2xl">👤</span>, bg: 'bg-blue-500', border: 'border-blue-200', hover: 'hover:bg-blue-100', bgLight: 'bg-blue-50' },
                { type: 'instagram' as const, label: 'Instagram', desc: 'Connect your Instagram Business account', icon: <span className="text-2xl">📷</span>, bg: 'bg-purple-500', border: 'border-purple-200', hover: 'hover:bg-purple-100', bgLight: 'bg-purple-50' },
                { type: 'whatsapp' as const, label: 'WhatsApp Business', desc: 'Connect your WhatsApp Business API', icon: <span className="text-2xl">💬</span>, bg: 'bg-green-500', border: 'border-green-200', hover: 'hover:bg-green-100', bgLight: 'bg-green-50' },
              ].map(({ type, label, desc, icon, bg, border, hover, bgLight }) => (
                <button
                  key={type}
                  onClick={() => handleConnect(type)}
                  className={`w-full flex items-center gap-4 p-4 ${bgLight} ${hover} border ${border} rounded-xl transition-all`}
                >
                  <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center`}>{icon}</div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold text-gray-900">{label}</h3>
                    <p className="text-sm text-gray-500">{desc}</p>
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>

            <p className="text-xs text-gray-400 text-center mt-6">Select a channel type to get started</p>
          </div>
        </div>
      )}

      {/* Custom API Channel Create Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCustomModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Create Custom API Channel</h2>
              </div>
              <button onClick={() => setShowCustomModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Channel Name <span className="text-red-500">*</span></label>
                <input type="text" value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Mobile App, Website Chat, Partner API" className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Webhook URL <span className="text-gray-400">(optional - for async mode)</span></label>
                <input type="url" value={customWebhookUrl} onChange={(e) => setCustomWebhookUrl(e.target.value)} placeholder="https://your-server.com/webhook" className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
                <p className="text-xs text-gray-400 mt-1">{customWebhookUrl.trim() ? 'Async mode: AI responses will be sent to this webhook URL with HMAC signing' : 'Sync mode: AI responses will be returned in the API response directly'}</p>
              </div>

              <div className={`p-3 rounded-xl ${customWebhookUrl.trim() ? 'bg-purple-50 border border-purple-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {customWebhookUrl.trim() ? (
                    <>
                      <Webhook className="w-4 h-4 text-purple-600" />
                      <div>
                        <p className="text-sm font-medium text-purple-700">Async Mode</p>
                        <p className="text-xs text-purple-600">Returns conversation_id immediately, sends AI response to your webhook</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <Zap className="w-4 h-4 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-amber-700">Sync Mode</p>
                        <p className="text-xs text-amber-600">Returns full AI response in the API call (may take a few seconds)</p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign Agents <span className="text-gray-400">(optional)</span></label>
                <AgentMultiSelect agents={activeAgents} selectedIds={customAgentIds} onChange={setCustomAgentIds} accentColor="indigo" placeholder="Select agents..." />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowCustomModal(false)} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm">Cancel</button>
              <button onClick={handleCreateCustomChannel} disabled={isCreatingCustom || !customName.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 text-white font-semibold rounded-xl hover:bg-indigo-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                {isCreatingCustom ? (<><Loader2 className="w-4 h-4 animate-spin" />Creating...</>) : (<><Zap className="w-4 h-4" />Create Channel</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Telegram Connect Modal */}
      {showTelegramModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowTelegramModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sky-500 rounded-xl flex items-center justify-center">
                  <Send className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Connect Telegram Bot</h2>
              </div>
              <button onClick={() => setShowTelegramModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Bot Token <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={showToken ? 'text' : 'password'} value={botToken} onChange={(e) => setBotToken(e.target.value)} placeholder="Paste your bot token from @BotFather" className="w-full px-4 py-2.5 pr-10 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
                  <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Get this from <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-sky-500 hover:underline">@BotFather</a> on Telegram</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign Agents <span className="text-gray-400">(optional)</span></label>
                <AgentMultiSelect agents={activeAgents} selectedIds={selectedAgentIds} onChange={setSelectedAgentIds} accentColor="sky" placeholder="Select agents..." />
                {activeAgents.length === 0 && <p className="text-xs text-amber-600 mt-1">No active agents found. Please create and activate an agent first.</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Name <span className="text-gray-400">(optional)</span></label>
                <input type="text" value={botName} onChange={(e) => setBotName(e.target.value)} placeholder="e.g. Support Bot" className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setShowTelegramModal(false)} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm">Cancel</button>
              <button onClick={handleTelegramConnect} disabled={isConnecting || !botToken.trim()} className="inline-flex items-center gap-2 px-5 py-2.5 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm">
                {isConnecting ? (<><Loader2 className="w-4 h-4 animate-spin" />Connecting...</>) : (<><Send className="w-4 h-4" />Connect</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Meta Connect Modal — Compact Rows */}
      {showMetaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isMetaLoading && setShowMetaModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${metaInitChannelType === 'instagram' ? 'bg-purple-500' : 'bg-blue-500'}`}>
                  <span className="text-xl">{metaInitChannelType === 'instagram' ? '📷' : '👤'}</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  Connect {metaInitChannelType === 'instagram' ? 'Instagram' : 'Facebook'}
                </h2>
              </div>
              <button onClick={() => !isMetaLoading && setShowMetaModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {metaStep === 'auth' && (
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-amber-900 mb-2">How it works</h3>
                  <ol className="text-sm text-amber-800 space-y-1.5 list-decimal list-inside">
                    <li>Click below to authorize with Facebook</li>
                    <li>Grant access to your pages</li>
                    <li>Select channels and assign agents</li>
                  </ol>
                </div>

                <button onClick={handleMetaAuth} disabled={isMetaLoading} className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50">
                  {isMetaLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Authorizing...</>) : (<><ExternalLink className="w-5 h-5" />Connect with Facebook</>)}
                </button>

                <p className="text-xs text-gray-400 text-center">Both Facebook Messenger and Instagram DM use the same authorization.</p>
              </div>
            )}

            {metaStep === 'pages' && (
              <div className="space-y-3">
                {metaPages.length === 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
                    <p className="text-sm text-amber-800">No Facebook pages found. Make sure you have admin access to at least one Facebook page.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 mb-2">
                      {metaPages.length} page{metaPages.length > 1 ? 's' : ''} found. Select channels and assign agents:
                    </p>

                    {/* Compact row header */}
                    <div className="grid grid-cols-[1fr,auto,auto,auto] gap-2 px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      <span>Page</span>
                      <span className="w-[90px] text-center">Messenger</span>
                      <span className="w-[90px] text-center">Instagram</span>
                      <span className="w-[180px]">Agent</span>
                    </div>

                    {/* Compact rows */}
                    <div className="space-y-1">
                      {metaPages.map(page => {
                        const config = metaPageConfig[page.id] || { facebook: false, instagram: false, agent_ids: [] }
                        const alreadyFb = channels.some(ch => ch.channel_type === 'facebook' && ch.credentials?.page_id === page.id)
                        const alreadyIg = channels.some(ch => ch.channel_type === 'instagram' && ch.credentials?.page_id === page.id)
                        const hasSelection = (config.facebook && !alreadyFb) || (config.instagram && !alreadyIg)

                        return (
                          <div
                            key={page.id}
                            className={`grid grid-cols-[1fr,auto,auto,auto] gap-2 items-center px-3 py-2.5 rounded-xl border transition-all ${
                              hasSelection ? 'border-amber-200 bg-amber-50/40' : 'border-gray-100 bg-gray-50/30'
                            }`}
                          >
                            {/* Page name */}
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-base flex-shrink-0">📰</span>
                              <span className="text-sm font-medium text-gray-900 truncate">{page.name}</span>
                            </div>

                            {/* Messenger toggle */}
                            <div className="w-[90px] flex justify-center">
                              {alreadyFb ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                                  <CheckCircle className="w-2.5 h-2.5" /> Connected
                                </span>
                              ) : (
                                <button
                                  onClick={() => setMetaPageConfig(prev => ({ ...prev, [page.id]: { ...prev[page.id], facebook: !config.facebook } }))}
                                  className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                    config.facebook
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}
                                >
                                  {config.facebook ? '✓ On' : 'Off'}
                                </button>
                              )}
                            </div>

                            {/* Instagram toggle */}
                            <div className="w-[90px] flex justify-center">
                              {!page.instagram_account ? (
                                <span className="text-[10px] text-gray-300">N/A</span>
                              ) : alreadyIg ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">
                                  <CheckCircle className="w-2.5 h-2.5" /> Connected
                                </span>
                              ) : (
                                <button
                                  onClick={() => setMetaPageConfig(prev => ({ ...prev, [page.id]: { ...prev[page.id], instagram: !config.instagram } }))}
                                  className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                                    config.instagram
                                      ? 'bg-purple-600 text-white'
                                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}
                                >
                                  {config.instagram ? '✓ On' : 'Off'}
                                </button>
                              )}
                            </div>

                            {/* Agent selector */}
                            <div className="w-[180px]">
                              {hasSelection ? (
                                <AgentMultiSelect
                                  agents={activeAgents}
                                  selectedIds={config.agent_ids}
                                  onChange={(ids) => setMetaPageConfig(prev => ({ ...prev, [page.id]: { ...prev[page.id], agent_ids: ids } }))}
                                  accentColor="amber"
                                  size="compact"
                                  placeholder="Agents..."
                                />
                              ) : (
                                <span className="text-xs text-gray-300">--</span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )}

                <div className="flex items-center justify-between pt-3">
                  <button onClick={() => setShowMetaModal(false)} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm">Cancel</button>
                  <button
                    onClick={handleMetaConnect}
                    disabled={!Object.entries(metaPageConfig).some(([pageId, c]) => {
                      const alreadyFb = channels.some(ch => ch.channel_type === 'facebook' && ch.credentials?.page_id === pageId)
                      const alreadyIg = channels.some(ch => ch.channel_type === 'instagram' && ch.credentials?.page_id === pageId)
                      return (c.facebook && !alreadyFb && c.agent_ids.length > 0) || (c.instagram && !alreadyIg && c.agent_ids.length > 0)
                    })}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Connect Selected
                  </button>
                </div>
              </div>
            )}

            {metaStep === 'connecting' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin mb-4" />
                <p className="text-sm text-gray-600">{metaConnectProgress || 'Connecting your pages...'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Connect Modal */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => !isWhatsAppLoading && setShowWhatsAppModal(false)} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500 flex items-center justify-center">
                  <span className="text-xl">💬</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Connect WhatsApp</h2>
              </div>
              <button onClick={() => !isWhatsAppLoading && setShowWhatsAppModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {whatsappStep === 'auth' && (
              <div className="space-y-4">
                <div className="bg-green-50 rounded-lg p-4 text-sm text-green-800 space-y-2">
                  <p className="font-medium">To connect WhatsApp Business:</p>
                  <ol className="list-decimal ml-4 space-y-1 text-green-700">
                    <li>Sign in with your Meta account</li>
                    <li>Complete the WhatsApp Business setup</li>
                    <li>Assign agents to handle conversations</li>
                  </ol>
                </div>
                <button
                  onClick={handleWhatsAppAuth}
                  disabled={isWhatsAppLoading}
                  className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50"
                >
                  {isWhatsAppLoading ? (<><Loader2 className="w-5 h-5 animate-spin" />Connecting...</>) : (<><ExternalLink className="w-5 h-5" />Connect with Meta</>)}
                </button>
                <p className="text-xs text-gray-400 text-center">Uses Meta Embedded Signup for WhatsApp Business.</p>
              </div>
            )}

            {whatsappStep === 'phone' && (
              <div className="space-y-4">
                {whatsappWabaName ? (
                  <div className="bg-green-50 rounded-lg p-3">
                    <p className="text-sm font-medium text-green-800">Connected to {whatsappWabaName}</p>
                  </div>
                ) : whatsappMessage ? (
                  <div className="bg-amber-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm font-medium text-amber-800">{whatsappMessage}</p>
                    <p className="text-xs text-amber-600">You can set up WhatsApp Business at <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">business.facebook.com</a> and come back here to connect.</p>
                    <button onClick={() => { setWhatsappStep('auth'); setWhatsappMessage(''); }} className="text-xs font-semibold text-amber-700 hover:text-amber-900 mt-1">← Try again</button>
                  </div>
                ) : null}

                {/* Phone number selection — multi-select like Meta pages */}
                {whatsappPhones.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Select phone numbers to connect:</p>
                    <div className="space-y-2">
                      {whatsappPhones.map(phone => {
                        const isSelected = selectedWhatsappPhoneIds.has(phone.id)
                        return (
                          <button
                            key={phone.id}
                            onClick={() => {
                              const next = new Set(selectedWhatsappPhoneIds)
                              if (isSelected) next.delete(phone.id); else next.add(phone.id)
                              setSelectedWhatsappPhoneIds(next)
                            }}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}
                          >
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? 'bg-green-500 border-green-500' : 'border-gray-300'}`}>
                              {isSelected && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                            </div>
                            <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-sm">💬</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900">{phone.display_phone_number}</p>
                              <p className="text-xs text-gray-500">{phone.verified_name}</p>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )}

                {whatsappPhones.length === 0 && !whatsappMessage && (
                  <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">No phone numbers found. Please complete WhatsApp Business setup first.</p>
                )}

                {/* Agent selection — shown when at least one phone selected */}
                {selectedWhatsappPhoneIds.size > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Assign Agents (applied to all selected numbers)</label>
                    <AgentMultiSelect
                      agents={agents}
                      selectedIds={whatsappAgentIds}
                      onChange={setWhatsappAgentIds}
                      placeholder="Select agents..."
                      accentColor="amber"
                    />
                  </div>
                )}

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button onClick={() => { setWhatsappStep('auth'); setWhatsappPhones([]); setSelectedWhatsappPhoneIds(new Set()); setWhatsappWabaName(''); setWhatsappMessage('') }} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm">Back</button>
                  {selectedWhatsappPhoneIds.size > 0 && (
                    <button
                      onClick={handleWhatsAppConnect}
                      disabled={isWhatsAppLoading || selectedWhatsappPhoneIds.size === 0}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      {isWhatsAppLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />Connecting...</>) : `Connect ${selectedWhatsappPhoneIds.size} number${selectedWhatsappPhoneIds.size > 1 ? 's' : ''}`}
                    </button>
                  )}
                </div>
              </div>
            )}

            {whatsappStep === 'connecting' && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-10 h-10 text-green-600 animate-spin mb-4" />
                <p className="text-sm text-gray-600">Connecting your WhatsApp number...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent Management Modal */}
      {showAgentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAgentModal(null)} />
          <div className="relative bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-gray-900">Manage Channel Agents</h2>
              <button onClick={() => setShowAgentModal(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {(() => {
              const channel = channels.find(c => c.id === showAgentModal)
              if (!channel) return null
              const channelAgents = channel.agents || []
              const assignedAgentIds = channelAgents.map(ca => ca.agent_id)
              const availableAgents = activeAgents.filter(a => !assignedAgentIds.includes(a.id))

              return (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Assigned Agents</h3>
                    {channelAgents.length === 0 ? (
                      <p className="text-sm text-gray-400 py-3 text-center bg-gray-50 rounded-lg">No agents assigned</p>
                    ) : (
                      <div className="space-y-2">
                        {channelAgents.map((ca) => {
                          const agent = agents.find(a => a.id === ca.agent_id)
                          return (
                            <div key={ca.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Bot className="w-4 h-4 text-amber-600" />
                                <span className="text-sm font-medium text-gray-900">{agent?.name || 'Unknown'}</span>
                                <span className="text-xs text-gray-400 capitalize">({agent?.type || ''})</span>
                              </div>
                              <button onClick={() => handleRemoveAgent(channel.id, ca.agent_id)} className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {availableAgents.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Add Agent</h3>
                      <div className="flex gap-2">
                        <select value={addingAgentId} onChange={(e) => setAddingAgentId(e.target.value)} className="flex-1 px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm bg-white">
                          <option value="">Select agent...</option>
                          {availableAgents.map(agent => (<option key={agent.id} value={agent.id}>{agent.name} ({agent.type})</option>))}
                        </select>
                        <button onClick={() => handleAddAgent(channel.id)} disabled={!addingAgentId || isAddingAgent} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 border-2 border-[#1a1a2e] disabled:opacity-50 flex items-center gap-1">
                          {isAddingAgent ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })()}
          </div>
        </div>
      )}

      {/* Channel Configure Modal */}
      {showDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => closeConfigModal()} />
          <div className="relative bg-white rounded-xl p-6 max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getChannelIconBg(showDetailModal.channel_type)}`}>
                  {getChannelIcon(showDetailModal.channel_type)}
                </div>
                <h2 className="text-lg font-bold text-gray-900">Configure Channel</h2>
              </div>
              <button onClick={() => closeConfigModal()} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Type</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{showDetailModal.channel_type}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-0.5">Status</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{showDetailModal.status}</p>
                </div>
              </div>

              {/* Channel Error Alert */}
              {Boolean(showDetailModal.settings?.last_error) && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-red-700 mb-0.5">Last Error</p>
                      <p className="text-xs text-red-600 break-words">{String(showDetailModal.settings.last_error)}</p>
                      {Boolean(showDetailModal.settings.last_error_at) && (
                        <p className="text-[10px] text-red-400 mt-1">
                          {new Date(String(showDetailModal.settings.last_error_at)).toLocaleString()}
                          {showDetailModal.settings.error_count ? ` (${String(showDetailModal.settings.error_count)} errors)` : ''}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={async () => {
                        try {
                          await channelsService.updateChannel(showDetailModal.id, { settings: { last_error: null, last_error_at: null, error_count: 0 } })
                          toast.success('Error cleared')
                          await fetchChannels()
                          setShowDetailModal(prev => prev ? { ...prev, settings: { ...prev.settings, last_error: undefined, last_error_at: undefined, error_count: undefined } } : null)
                        } catch (_) {
                          toast.error('Failed to clear error')
                        }
                      }}
                      className="flex-shrink-0 text-[10px] font-medium text-red-600 hover:text-red-700 hover:bg-red-100 px-2 py-1 rounded-lg transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Channel Name</label>
                <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
              </div>

              {showDetailModal.channel_type === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Response Mode</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setEditResponseMode('sync')} className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${editResponseMode === 'sync' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <Zap className={`w-4 h-4 ${editResponseMode === 'sync' ? 'text-amber-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`text-sm font-medium ${editResponseMode === 'sync' ? 'text-amber-700' : 'text-gray-700'}`}>Sync</p>
                          <p className="text-xs text-gray-500">Response in API call</p>
                        </div>
                      </button>
                      <button type="button" onClick={() => setEditResponseMode('async')} className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${editResponseMode === 'async' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <Webhook className={`w-4 h-4 ${editResponseMode === 'async' ? 'text-purple-600' : 'text-gray-400'}`} />
                        <div>
                          <p className={`text-sm font-medium ${editResponseMode === 'async' ? 'text-purple-700' : 'text-gray-700'}`}>Async</p>
                          <p className="text-xs text-gray-500">Response via webhook</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Webhook URL {editResponseMode === 'async' && <span className="text-red-500">*</span>}</label>
                    <input type="url" value={editWebhookUrl} onChange={(e) => setEditWebhookUrl(e.target.value)} placeholder="https://your-server.com/webhook" className="w-full px-4 py-2.5 border-2 border-[#1a1a2e] rounded-xl focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e] outline-none text-sm" />
                    {editResponseMode === 'async' && !editWebhookUrl.trim() && <p className="text-xs text-amber-600 mt-1">Webhook URL is required for async mode</p>}
                  </div>
                </>
              )}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Assigned Agents</label>
                  <button onClick={() => { setShowDetailModal(null); setShowAgentModal(showDetailModal.id); navigate('/channels'); }} className="text-xs text-amber-600 hover:text-amber-700 font-medium">Manage Agents</button>
                </div>
                {(showDetailModal.agents && showDetailModal.agents.length > 0) ? (
                  <div className="flex flex-wrap gap-1.5">
                    {showDetailModal.agents.map((ca) => (
                      <span key={ca.id} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 rounded-lg text-xs font-medium">
                        <Bot className="w-3 h-3" />
                        {getAgentName(ca.agent_id) || 'Unknown'}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 py-2 text-center bg-gray-50 rounded-lg">No agents assigned</p>
                )}
              </div>

              {showDetailModal.channel_token && (
                <>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs text-gray-500">Channel Token</p>
                      <button onClick={() => copyToClipboard(showDetailModal.channel_token!, 'Token')} className="text-xs text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1">
                        {copiedToken === showDetailModal.channel_token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy
                      </button>
                    </div>
                    <code className="text-xs text-gray-700 font-mono break-all">{showDetailModal.channel_token}</code>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 mb-1">API Endpoint</p>
                    <code className="text-xs text-amber-700 font-mono break-all">POST /api/chat/public/{showDetailModal.channel_token}</code>
                    <p className="text-xs text-gray-400 mt-2">Send: {'{ "customer_id": "...", "message": "..." }'}</p>
                  </div>
                </>
              )}

              {/* Chat Widget Section — custom channels with token only */}
              {showDetailModal.channel_type === 'custom' && showDetailModal.channel_token && (
                <div className="border-2 border-[#1a1a2e] rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b-2 border-[#1a1a2e]">
                    <MessageSquare className="w-4 h-4 text-indigo-600" />
                    <span className="text-sm font-semibold text-gray-900">Chat Widget</span>
                  </div>

                  {/* Tabs */}
                  <div className="flex border-b-2 border-[#1a1a2e]">
                    <button onClick={() => setWidgetTab('theme')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors ${widgetTab === 'theme' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' : 'text-gray-500 hover:text-gray-700'}`}>
                      <Palette className="w-3.5 h-3.5" /> Theme
                    </button>
                    <button onClick={() => setWidgetTab('embed')} className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold transition-colors ${widgetTab === 'embed' ? 'text-indigo-600 border-b-2 border-indigo-600 bg-indigo-50/50' : 'text-gray-500 hover:text-gray-700'}`}>
                      <Code className="w-3.5 h-3.5" /> Embed Code
                    </button>
                  </div>

                  <div className="p-4 space-y-4">
                    {widgetTab === 'theme' && (
                      <>
                        {/* Color picker */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Primary Color</label>
                          <div className="flex flex-wrap gap-2">
                            {['#2563eb','#7c3aed','#059669','#dc2626','#ea580c','#0891b2','#4f46e5','#be185d'].map(color => (
                              <button
                                key={color}
                                onClick={() => setWidgetColor(color)}
                                className={`w-8 h-8 rounded-lg transition-all ${widgetColor === color ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-105'}`}
                                style={{ background: color }}
                              />
                            ))}
                            <div className="relative">
                              <input
                                type="color"
                                value={widgetColor}
                                onChange={(e) => setWidgetColor(e.target.value)}
                                className="w-8 h-8 rounded-lg cursor-pointer border-2 border-[#1a1a2e]"
                                title="Custom color"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Bubble text */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Bubble Text</label>
                          <input type="text" value={widgetBubbleText} onChange={(e) => setWidgetBubbleText(e.target.value)} placeholder="Chat with us" className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]" />
                        </div>

                        {/* Welcome message */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Welcome Message</label>
                          <input type="text" value={widgetWelcome} onChange={(e) => setWidgetWelcome(e.target.value)} placeholder="Hi! How can I help?" className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]" />
                        </div>

                        {/* Header text */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Header Text</label>
                          <input type="text" value={widgetHeaderText} onChange={(e) => setWidgetHeaderText(e.target.value)} placeholder="Support Agent" className="w-full px-3 py-2 border-2 border-[#1a1a2e] rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#1a1a2e]/20 focus:border-[#1a1a2e]" />
                        </div>

                        {/* Position toggle */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Position</label>
                          <div className="grid grid-cols-2 gap-2">
                            <button type="button" onClick={() => setWidgetPosition('bottom-right')} className={`flex items-center justify-center gap-1.5 p-2.5 rounded-lg border-2 text-xs font-medium transition-all ${widgetPosition === 'bottom-right' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                              Bottom Right
                            </button>
                            <button type="button" onClick={() => setWidgetPosition('bottom-left')} className={`flex items-center justify-center gap-1.5 p-2.5 rounded-lg border-2 text-xs font-medium transition-all ${widgetPosition === 'bottom-left' ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                              Bottom Left
                            </button>
                          </div>
                        </div>

                        {/* Mini preview */}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-2">Preview</label>
                          <div className="relative bg-gray-100 rounded-xl p-4 h-32 overflow-hidden">
                            <div className={`absolute bottom-3 ${widgetPosition === 'bottom-right' ? 'right-3' : 'left-3'} flex items-center gap-2 ${widgetPosition === 'bottom-right' ? 'flex-row-reverse' : ''}`}>
                              <div className="w-10 h-10 rounded-full flex items-center justify-center border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]" style={{ background: widgetColor }}>
                                <MessageSquare className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-[11px] font-medium text-white px-2.5 py-1 rounded-full border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e]" style={{ background: widgetColor }}>
                                {widgetBubbleText || 'Chat with us'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {widgetTab === 'embed' && (
                      <>
                        {/* Script tag */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-medium text-gray-700">Script Tag (Floating Bubble)</label>
                            <button
                              onClick={() => copyEmbed(`<script src="${getApiBase()}/widget.js" data-token="${showDetailModal.channel_token}"></script>`, 'script')}
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                            >
                              {copiedEmbed === 'script' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy
                            </button>
                          </div>
                          <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto font-mono">
{`<script src="${getApiBase()}/widget.js"
  data-token="${showDetailModal.channel_token}">
</script>`}
                          </pre>
                          <p className="text-[11px] text-gray-400 mt-1.5">Add this before {'</body>'} on your website. Creates a floating chat bubble.</p>
                        </div>

                        {/* Iframe embed */}
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <label className="text-xs font-medium text-gray-700">Iframe Embed (Inline)</label>
                            <button
                              onClick={() => copyEmbed(`<iframe src="${getApiBase()}/widget/chat/${showDetailModal.channel_token}" style="width:400px;height:600px;border:none;border-radius:16px;" allow="clipboard-write"></iframe>`, 'iframe')}
                              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium flex items-center gap-1"
                            >
                              {copiedEmbed === 'iframe' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} Copy
                            </button>
                          </div>
                          <pre className="bg-gray-900 text-green-400 text-xs p-3 rounded-lg overflow-x-auto font-mono">
{`<iframe
  src="${getApiBase()}/widget/chat/${showDetailModal.channel_token}"
  style="width:400px;height:600px;
    border:none;border-radius:16px;"
  allow="clipboard-write">
</iframe>`}
                          </pre>
                          <p className="text-[11px] text-gray-400 mt-1.5">Embed the chat directly into your page layout.</p>
                        </div>

                        {/* Data attributes reference */}
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs font-medium text-gray-700 mb-2">Optional Data Attributes</p>
                          <div className="space-y-1 text-[11px] font-mono text-gray-600">
                            <p><span className="text-indigo-600">data-color</span>="#2563eb" — Override primary color</p>
                            <p><span className="text-indigo-600">data-position</span>="bottom-left" — Bubble position</p>
                            <p><span className="text-indigo-600">data-bubble-text</span>="Hi!" — Bubble label</p>
                            <p><span className="text-indigo-600">data-welcome</span>="Hello!" — Welcome message</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {showDetailModal.credentials?.bot_username && (
                <div className="bg-sky-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Telegram Bot</p>
                  <p className="text-sm font-medium text-gray-900">@{showDetailModal.credentials.bot_username}</p>
                </div>
              )}

              {showDetailModal.credentials?.page_id && (
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">{showDetailModal.channel_type === 'instagram' ? 'Instagram Account' : 'Facebook Page'}</p>
                  <p className="text-sm font-medium text-gray-900">{showDetailModal.credentials.page_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Page ID: {showDetailModal.credentials.page_id}</p>
                  {showDetailModal.credentials.instagram_account_id && <p className="text-xs text-gray-500">IG Account: {showDetailModal.credentials.instagram_account_id}</p>}
                </div>
              )}

              {showDetailModal.channel_type === 'whatsapp' && (showDetailModal.credentials as any)?.phone_number_id && (
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">WhatsApp Business</p>
                  <p className="text-sm font-medium text-gray-900">{(showDetailModal.credentials as any).display_name || 'Unknown'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Phone: {(showDetailModal.credentials as any).phone_number || 'N/A'}</p>
                  <p className="text-xs text-gray-500">Phone Number ID: {(showDetailModal.credentials as any).phone_number_id}</p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button onClick={() => closeConfigModal()} className="px-4 py-2.5 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors text-sm">Cancel</button>
              <button onClick={handleSaveConfig} disabled={isSaving} className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] disabled:opacity-50 text-sm">
                {isSaving ? (<><Loader2 className="w-4 h-4 animate-spin" />Saving...</>) : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ChannelsPage
